import { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, Spinner,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getVIRCategories, getVIRVendorsByCategory } from './FormikHelpers/Api';

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    borderColor: isFocused ? '#4F46E5' : '#E2E8F0',
    borderRadius: 8, minHeight: 36, fontSize: '0.83rem',
    boxShadow: isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
    '&:hover': { borderColor: '#4F46E5' },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base, fontSize: '0.83rem',
    background: isSelected ? '#4F46E5' : isFocused ? '#EEF2FF' : 'white',
    color: isSelected ? 'white' : '#0F172A',
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: '1px solid #E2E8F0', zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const VendorCard = ({ vendor }) => (
  <div style={{
    background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 18px',
    marginBottom: 10, boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0F172A' }}>{vendor.vendorName}</div>
        {vendor.companyName && (
          <div style={{ fontSize: '0.78rem', color: '#64748B', marginTop: 2 }}>{vendor.companyName}</div>
        )}
      </div>
      <span style={{
        background: vendor.isActive ? '#ECFDF5' : '#FEF2F2',
        color: vendor.isActive ? '#059669' : '#DC2626',
        border: `1px solid ${vendor.isActive ? '#6EE7B7' : '#FECDD3'}`,
        borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
      }}>
        {vendor.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {vendor.representatives && vendor.representatives.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {vendor.representatives.map((rep, i) => (
            <div key={i} style={{ fontSize: '0.76rem', color: '#475569', paddingTop: i > 0 ? 4 : 0, borderTop: i > 0 ? '1px dashed #E2E8F0' : 'none' }}>
              {rep.contactName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <FeatherIcon icon="user" size={11} color="#94A3B8" />
                  <span style={{ fontWeight: 600 }}>{rep.contactName}</span>
                  {rep.title && <span style={{ color: '#94A3B8' }}>· {rep.title}</span>}
                </div>
              )}
              {rep.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
                  <FeatherIcon icon="mail" size={11} color="#94A3B8" />
                  <a href={`mailto:${rep.email}`} style={{ color: '#4F46E5' }}>{rep.email}</a>
                </div>
              )}
              {rep.phoneNo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
                  <FeatherIcon icon="phone" size={11} color="#94A3B8" />
                  {rep.phoneNo}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {vendor.website && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.80rem', color: '#475569' }}>
          <FeatherIcon icon="globe" size={13} color="#94A3B8" />
          <a href={vendor.website} target="_blank" rel="noopener noreferrer" style={{ color: '#4F46E5' }}>
            {vendor.website.replace(/^https?:\/\//, '')}
          </a>
        </div>
      )}
    </div>
    {Array.isArray(vendor.categoryIds) && vendor.categoryIds.length > 0 && (
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {vendor.categoryIds.map((cat, i) => (
          <span key={i} style={{
            background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE',
            borderRadius: 6, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 600,
          }}>
            {typeof cat === 'object' ? cat.name : cat}
          </span>
        ))}
      </div>
    )}
  </div>
);

const VendorsByCategoryModal = ({ isOpen, onClose }) => {
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catLoading, setCatLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    setCatLoading(true);
    try {
      const res = await getVIRCategories({ activeOnly: 'true' });
      setCategoryOptions((res?.data || []).map((c) => ({ value: c._id, label: c.name })));
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setCatLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchCategories();
  }, [isOpen, fetchCategories]);

  useEffect(() => {
    if (!selectedCategory) { setVendors([]); return; }
    setLoading(true);
    getVIRVendorsByCategory(selectedCategory.value)
      .then((res) => setVendors(res?.data || []))
      .catch(() => toast.error('Failed to load vendors'))
      .finally(() => setLoading(false));
  }, [selectedCategory]);

  const handleClose = () => {
    setSelectedCategory(null);
    setVendors([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader
        toggle={handleClose}
        style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 100%)', borderRadius: '10px 10px 0 0', padding: '16px 24px', borderBottom: '2px solid #4F46E5' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FeatherIcon icon="briefcase" size={16} color="#A5B4FC" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'white' }}>Vendors by Category</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(165,180,252,0.6)', marginTop: 1 }}>Filter vendors by inspection category</div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '24px', background: '#F8FAFC', minHeight: 360 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' }}>
            Select Category
          </label>
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder={catLoading ? 'Loading categories...' : 'Choose a category...'}
            styles={selectStyles}
            isLoading={catLoading}
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {!selectedCategory && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
            <FeatherIcon icon="briefcase" size={36} color="#CBD5E1" />
            <div style={{ marginTop: 12, fontSize: '0.88rem', fontWeight: 600 }}>Select a category to see vendors</div>
          </div>
        )}

        {selectedCategory && loading && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Spinner style={{ color: '#4F46E5' }} />
          </div>
        )}

        {selectedCategory && !loading && vendors.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
            <FeatherIcon icon="user-x" size={36} color="#CBD5E1" />
            <div style={{ marginTop: 12, fontSize: '0.88rem', fontWeight: 600 }}>No vendors found for this category</div>
          </div>
        )}

        {selectedCategory && !loading && vendors.length > 0 && (
          <div>
            <div style={{ fontSize: '0.78rem', color: '#64748B', marginBottom: 12, fontWeight: 600 }}>
              {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} found
            </div>
            {vendors.map((v) => <VendorCard key={v._id} vendor={v} />)}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export { VendorsByCategoryModal };
